import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime, safeHref } from "@/lib/format";
import { PageHead, Panel, Pill, KV, Sel, Btn } from "@/components/admin/kit";
import { ActivityList, NotificationList, NotesList } from "@/components/admin/Lists";
import { NoteForm } from "@/components/admin/NoteForm";
import { QuoteBuilder, quoteDefaults } from "@/components/admin/QuoteBuilder";
import { QuoteCard } from "@/components/admin/QuoteCard";
import { Loading } from "@/components/site/Primitives";
import { ENQUIRY_STATUSES } from "./Enquiries";

const Link2 = ({ url }) => url ? <a href={safeHref(url)} target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] underline break-all">{url}</a> : null;

export default function EnquiryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [users, setUsers] = useState([]);
  const [ctx, setCtx] = useState({ services: [], pricing: null });
  const [builder, setBuilder] = useState(null);

  const load = useCallback(() => api.get(`/admin/enquiries/${id}`).then((r) => setD(r.data)), [id]);
  useEffect(() => {
    load();
    api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.active)));
    Promise.all([api.get("/admin/services"), api.get("/admin/settings")]).then(([s, st]) => setCtx({ services: s.data, pricing: st.data.pricing }));
  }, [load]);

  const patch = async (body, msg) => {
    try { await api.patch(`/admin/enquiries/${id}`, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const post = async (path, body, msg) => {
    try { const r = await api.post(`/admin/enquiries/${id}/${path}`, body); toast.success(typeof msg === "function" ? msg(r.data) : msg); load(); return r.data; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };

  if (!d) return <Loading rows={2} />;
  const e = d.enquiry;
  const hasAccepted = d.quotes.some((q) => q.status === "accepted" && q.payment_status !== "pending");
  const openQuote = d.quotes.find((q) => ["draft", "sent"].includes(q.status));
  const svc = ctx.services.find((s) => s.name === e.service_name);
  const newQuote = () => setBuilder({ initial: quoteDefaults(e, svc, ctx.pricing) });
  const revise = (q) => { const { version, totals, created_at, created_by, ...rest } = q.versions[q.versions.length - 1]; setBuilder({ initial: rest, quoteId: q.id }); };

  return (
    <div data-testid="admin-enquiry-detail">
      <Link to="/admin/enquiries" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-4"><ArrowLeft size={14} />Enquiries</Link>
      <PageHead eyebrow={`Enquiry · ${fmtDateTime(e.created_at)}`} title={<span><span className="font-mono text-[#00E5FF] mr-3" data-testid="enquiry-reference">{e.reference}</span>{e.name}</span>}>
        <Pill status={e.status} testid="enquiry-status" />
        {d.project ? <Btn onClick={() => nav(`/admin/projects/${d.project.id}`)} data-testid="open-project-btn">Open project {d.project.reference}</Btn>
          : hasAccepted && <Btn onClick={async () => { const p = await post("convert", { notify_client: true }, "Project created"); if (p) nav(`/admin/projects/${p.id}`); }} data-testid="convert-project-btn">Convert to project</Btn>}
      </PageHead>
      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <Panel title="Brief" testid="enquiry-brief-panel">
            <dl>
              <KV k="Client" v={`${e.name} · ${e.email}`} testid="enquiry-client" />
              <KV k="Company" v={e.company} />
              <KV k="Service" v={`${e.service_name} (${e.service_category})`} />
              <KV k="Videos" v={e.video_count} />
              <KV k="Duration" v={e.duration} />
              <KV k="Platform / ratio" v={`${e.platform} · ${e.aspect_ratio}`} />
              <KV k="Delivery date" v={e.delivery_date && fmtDate(e.delivery_date)} />
              <KV k="Budget" v={e.budget_range} />
              <KV k="Objectives & brief" v={e.brief} testid="enquiry-brief-text" />
              <KV k="References" v={e.references} />
              <KV k="Footage link" v={<Link2 url={e.footage_link} />} />
              <KV k="Additional" v={e.additional} />
            </dl>
          </Panel>
          <Panel title="Quotes" action={!openQuote && !d.quotes.some((q) => q.status === "accepted") && <Btn onClick={newQuote} data-testid="create-quote-btn">Create quote</Btn>}>
            {d.quotes.length ? <div className="space-y-4">{d.quotes.map((q) => <QuoteCard key={q.id} quote={q} onRevise={() => revise(q)} onChange={load} />)}</div>
              : <p className="text-sm text-zinc-600">No quote yet. Verify the scope, then build a quote.</p>}
          </Panel>
          <Panel title="Status history">
            {(e.status_history || []).slice().reverse().map((h, i) => (
              <div key={i} className="flex justify-between py-1.5 text-sm"><span><Pill status={h.status} /> <span className="text-zinc-500 ml-2">{h.note}</span></span><span className="text-xs text-zinc-600">{h.by} · {fmtDateTime(h.at)}</span></div>
            ))}
          </Panel>
        </div>
        <div className="space-y-6">
          <Panel title="Manage">
            <div className="space-y-4">
              <label className="block space-y-1"><span className="eb-label !text-[10px]">Status</span>
                <Sel value={e.status} onChange={(v) => patch({ status: v }, "Status updated")} options={ENQUIRY_STATUSES.filter((s) => s !== "Converted to Project" || e.status === s)} testid="enquiry-status-select" /></label>
              <label className="block space-y-1"><span className="eb-label !text-[10px]">Owner</span>
                <Sel value={e.owner_id || ""} onChange={(v) => patch({ owner_id: v }, "Owner assigned")} options={users.map((u) => ({ value: u.id, label: u.name }))} placeholder="Unassigned" testid="enquiry-owner-select" /></label>
              <label className="block space-y-1"><span className="eb-label !text-[10px]">Scope verification</span>
                <Sel value={e.scope_status} onChange={(v) => patch({ scope_status: v }, "Scope updated")} options={[{ value: "pending_verification", label: "Pending verification" }, { value: "within_scope", label: "Within standard scope" }, { value: "exceeds_scope", label: "Exceeds scope — custom quote" }]} testid="enquiry-scope-select" /></label>
            </div>
          </Panel>
          <Panel title="Request missing information">
            <NoteForm label="Email client" placeholder="What do you need from the client?" testid="request-info" onSubmit={(t) => post("request-info", { message: t }, (r) => `Request ${r.notification.status === "sent" ? "emailed" : "logged — email " + r.notification.status}`)} />
          </Panel>
          <Panel title="Private notes"><NotesList notes={e.notes} /><NoteForm testid="enquiry-note" onSubmit={(t) => post("notes", { text: t }, "Note added")} /></Panel>
          <Panel title="Emails"><NotificationList items={d.notifications} /></Panel>
          <Panel title="Activity"><ActivityList items={d.activity} /></Panel>
        </div>
      </div>
      {builder && <QuoteBuilder open onOpenChange={(o) => !o && setBuilder(null)} initial={builder.initial} quoteId={builder.quoteId} enquiryId={id} onSaved={load} />}
    </div>
  );
}
